import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Button, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import http from '../../http';

const { Option } = Select;

const OS_CHOICES = [
    { value: 'windows', label: 'Windows' },
    { value: 'linux', label: 'Linux' },
    { value: 'macos', label: 'macOS' },
    { value: 'other', label: 'Other' },
];

const ApplicationAddPage = ({ visible, onClose, onSuccessSubmit, edit, editItem }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [installerFile, setInstallerFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [logoFile, setLogoFile] = useState(null);
    const [logoFileList, setLogoFileList] = useState([]);

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setInstallerFile(null);
            setFileList([]);
            setLogoFile(null);
            setLogoFileList([]);
        }
    }, [visible]);

    useEffect(() => {
        if (edit && editItem) {
            http.get(`/api/terminal/applications/${editItem.id}/`).then(resp => {
                const data = resp.data;
                form.setFieldsValue({
                    name: data.name,
                    description: data.description,
                    os: data.os,
                    version: data.version,
                    release_notes: data.release_notes,
                    color_code: data.color_code,
                    bundle_id: data.bundle_id,
                    config: data.config && Object.keys(data.config).length
                        ? JSON.stringify(data.config, null, 2)
                        : '',
                });
                if (data.installer) {
                    const ext = data.installer.split('?')[0].split('.').pop();
                    const fileName = `${data.name}${data.version ? `_${data.version}` : ''}.${ext}`;
                    setFileList([{ uid: '-1', name: fileName, status: 'done', url: data.installer }]);
                }
                if (data.logo) {
                    setLogoFileList([{ uid: '-1', name: 'logo', status: 'done', url: data.logo }]);
                }
            }).catch(() => message.error('Failed to load application data'));
        }
    }, [edit, editItem]);

    const handleSubmit = (values) => {
        if (!edit && !installerFile) {
            message.error('Please select an installer file');
            return;
        }

        setLoading(true);
        const formData = new FormData();

        Object.entries(values).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (key === 'config') {
                const trimmed = String(value).trim();
                if (trimmed) formData.append(key, trimmed);
            } else if (key === 'released_at') {
                formData.append(key, value.toISOString());
            } else {
                formData.append(key, value);
            }
        });

        if (installerFile) {
            formData.append('installer', installerFile);
        }

        if (logoFile) {
            formData.append('logo', logoFile);
        }

        const request = edit
            ? http.patch(`/api/terminal/applications/${editItem.id}/`, formData)
            : http.post('/api/terminal/applications/upload/', formData);

        request.then(() => {
            message.success(edit ? 'Application updated' : 'Application created');
            setLoading(false);
            onSuccessSubmit();
        }).catch(err => {
            setLoading(false);
            const errData = err.response?.data;
            if (errData?.validation_error) {
                message.error(errData.validation_error);
            } else {
                message.error('Operation failed');
            }
        });
    };

    const handleCancel = () => {
        form.resetFields();
        setInstallerFile(null);
        setFileList([]);
        setLogoFile(null);
        setLogoFileList([]);
        onClose();
    };

    return (
        <Modal
            open={visible}
            title={edit ? `Edit ${editItem?.name || ''}` : 'Add Application'}
            onCancel={handleCancel}
            maskClosable={false}
            footer={[
                <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
                <Button key="submit" type="primary" onClick={() => form.submit()} loading={loading} style={{ background: '#377EF3' }}>
                    {edit ? 'Update' : 'Save'}
                </Button>,
            ]}
            width={600}
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                    <Input placeholder="Application name" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                    <Input.TextArea placeholder="Description" rows={2} />
                </Form.Item>
                <Form.Item label="Logo">
                    <Upload
                        listType="picture-card"
                        fileList={logoFileList}
                        beforeUpload={(file) => {
                            setLogoFile(file);
                            setLogoFileList([{
                                uid: file.uid,
                                name: file.name,
                                status: 'done',
                                url: URL.createObjectURL(file),
                            }]);
                            return false;
                        }}
                        onRemove={() => { setLogoFile(null); setLogoFileList([]); }}
                        maxCount={1}
                        accept="image/*"
                    >
                        {logoFileList.length ? null : <UploadOutlined />}
                    </Upload>
                </Form.Item>
                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item name="os" label="OS" style={{ flex: 1 }}>
                        <Select placeholder="Select OS" allowClear>
                            {OS_CHOICES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="version" label="Version" style={{ flex: 1 }}>
                        <Input placeholder="e.g. 1.0.0" />
                    </Form.Item>
                </div>
                <Form.Item label={`Installer${!edit ? ' *' : ''}`}>
                    <Upload
                        fileList={fileList}
                        beforeUpload={(file) => {
                            setInstallerFile(file);
                            setFileList([{ uid: file.uid, name: file.name, status: 'done' }]);
                            return false;
                        }}
                        onRemove={() => { setInstallerFile(null); setFileList([]); }}
                        onPreview={async (file) => {
                            if (!file.url) return;
                            try {
                                const res = await fetch(file.url);
                                const blob = await res.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = file.name;
                                a.click();
                                URL.revokeObjectURL(blobUrl);
                            } catch {
                                const a = document.createElement('a');
                                a.href = file.url;
                                a.download = file.name;
                                a.click();
                            }
                        }}
                        maxCount={1}
                        accept=".exe,.msi,.deb,.pkg,.dmg,.AppImage,.rpm,.zip"
                    >
                        <Button icon={<UploadOutlined />}>Select File</Button>
                    </Upload>
                </Form.Item>
                <Form.Item
                    name="bundle_id"
                    label="Bundle ID"
                    tooltip="Application bundle identifier (e.g. com.wakutech.app)"
                >
                    <Input placeholder="e.g. com.wakutech.app" />
                </Form.Item>
                <Form.Item name="release_notes" label="Release Notes">
                    <Input.TextArea placeholder="Release notes" rows={2} />
                </Form.Item>
                <Form.Item name="color_code" label="Color Code">
                    <Input
                        type="color"
                        style={{ width: 60, padding: 2, cursor: 'pointer' }}
                    />
                </Form.Item>
                <Form.Item
                    name="config"
                    label="Config"
                    tooltip="JSON written to the config file at install time"
                    rules={[{
                        validator: (_, value) => {
                            if (!value || !String(value).trim()) return Promise.resolve();
                            try {
                                JSON.parse(value);
                                return Promise.resolve();
                            } catch {
                                return Promise.reject(new Error('Invalid JSON'));
                            }
                        },
                    }]}
                >
                    <Input.TextArea placeholder='{"key": "value"}' rows={4} style={{ fontFamily: 'monospace' }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ApplicationAddPage;
